import { createFileRoute, Link } from "@tanstack/react-router";
import { BRAND } from "@/lib/behot";
import { WithdrawalForm } from "@/components/withdrawal-form";
import { LEGAL_OG_IMAGE, LEGAL_OG_IMAGE_ALT, buildPageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/withdrawal")({
  head: () =>
    buildPageMetadata({
      path: "/withdrawal",
      title: "Right of Withdrawal & Refund Policy | BeHot.Now",
      description:
        "Your EU right of withdrawal for BeHot.Now purchases: the 14-day cancellation window, exceptions for digital services that begin immediately, and how to request a refund.",
      ogTitle: "Right of Withdrawal & Refund Policy",
      keywords: [
        "right of withdrawal",
        "14 day refund",
        "EU consumer rights",
        "cancel digital purchase",
        "refund request",
      ],
      ogDescription: "14-day withdrawal, exceptions and how to request a refund.",
      image: LEGAL_OG_IMAGE,
      imageAlt: LEGAL_OG_IMAGE_ALT,
      twitterCard: "summary_large_image",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Right of withdrawal", path: "/withdrawal" },
      ],
    }),
  component: Withdrawal,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="font-display text-xl font-bold">{title}</h2>
      <div className="mt-2 space-y-3 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

function P({ text }: { text: string }) {
  return (
    <>
      {text.split("\n\n").map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </>
  );
}

function Withdrawal() {
  const { t, locale } = useT();
  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="font-display text-4xl font-bold">{t("legal.withdrawal.title")}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{t("legal.withdrawal.updated")}</p>
      <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
        {t("legal.withdrawal.intro.pre", { domain: BRAND.domain })}{" "}
        <strong className="text-foreground">{t("legal.withdrawal.intro.rightOfWithdrawal")}</strong>
        {t("legal.withdrawal.intro.post")}
      </p>

      <Section title={t("legal.withdrawal.s1.title")}>
        <P text={t("legal.withdrawal.s1.body", { domain: BRAND.domain })} />
      </Section>

      <Section title={t("legal.withdrawal.s2.title")}>
        <P text={t("legal.withdrawal.s2.body")} />
      </Section>

      <Section title={t("legal.withdrawal.s3.title")}>
        <p>{t("legal.withdrawal.s3.body1")}</p>
        <p>
          <strong className="text-foreground">{t("legal.withdrawal.s3.strong")}</strong>{" "}
          {t("legal.withdrawal.s3.body2", { domain: BRAND.domain })}
        </p>
      </Section>

      <Section title={t("legal.withdrawal.s4.title")}>
        <ul className="list-disc space-y-1 pl-5">
          <li>{t("legal.withdrawal.s4.item1")}</li>
          <li>{t("legal.withdrawal.s4.item2")}</li>
          <li>{t("legal.withdrawal.s4.item3")}</li>
        </ul>
      </Section>

      <Section title={t("legal.withdrawal.s5.title")}>
        <ul className="list-disc space-y-1 pl-5">
          <li>{t("legal.withdrawal.s5.item1")}</li>
          <li>{t("legal.withdrawal.s5.item2")}</li>
          <li>{t("legal.withdrawal.s5.item3")}</li>
          <li>{t("legal.withdrawal.s5.item4")}</li>
          <li>{t("legal.withdrawal.s5.item5")}</li>
        </ul>
      </Section>

      <Section title={t("legal.withdrawal.s6.title")}>
        <P text={t("legal.withdrawal.s6.body", { operator: BRAND.operator })} />
      </Section>

      <WithdrawalForm />

      <Section title={t("legal.withdrawal.s7.title")}>
        <P text={t("legal.withdrawal.s7.body")} />
      </Section>

      <Section title={t("legal.withdrawal.s8.title")}>
        <P text={t("legal.withdrawal.s8.body")} />
      </Section>

      <p className="mt-10 text-sm text-muted-foreground">
        {t("legal.withdrawal.footer.pre")}{" "}
        <Link to="/app/wallets" className="text-primary hover:underline">
          {t("legal.withdrawal.footer.wallets")}
        </Link>
        . {t("legal.withdrawal.footer.seeAlso")}{" "}
        <Link to="/terms" className="text-primary hover:underline">
          {t("legal.common.termsOfService")}
        </Link>
        ,{" "}
        <Link to="/privacy" className="text-primary hover:underline">
          {t("legal.common.privacyPolicy")}
        </Link>{" "}
        {t("legal.common.and")}{" "}
        <Link to="/cookie" className="text-primary hover:underline">
          {t("legal.common.cookiePolicy")}
        </Link>
        .
      </p>

      {locale !== "en" ? (
        <p className="mt-6 text-xs text-muted-foreground">{t("legal.common.englishPrevails")}</p>
      ) : null}
    </div>
  );
}
